import { useCallback, useState } from 'react';
/**
 * Owns the mention/slash typeahead state for the ask input: the (async) mention
 * menu open flag and the (synchronous) bare-trigger flag used to suppress the
 * suggest dropdown without racing the menu's async open signal.
 *
 * The bare-trigger flag is the source of truth for "trigger typed, no query yet":
 * - Set in the wrapped `onTriggerTypeahead`, which fires synchronously inside the
 *   plugin's `onTrigger` (before `onSearchQueryChange`) — so the dropdown can be
 *   suppressed in the same tick the trigger key is typed.
 * - Cleared in the wrapped `onMentionSearchQueryChange` once the typeahead is no
 *   longer on a bare trigger, and on mention menu close.
 */
export function useMentionTypeahead({ onTriggerTypeahead, onMentionSearchQueryChange, }) {
    const [isMentionMenuOpened, setIsMentionMenuOpen] = useState(false);
    const [isBareMentionTrigger, setIsBareMentionTrigger] = useState(false);
    const onMentionOpen = useCallback(() => setIsMentionMenuOpen(true), []);
    const onMentionClose = useCallback(() => {
        setIsMentionMenuOpen(false);
        setIsBareMentionTrigger(false);
    }, []);
    // Synchronously flag a bare trigger (trigger char typed with no query yet) so
    // the suggest dropdown defers to the mention popup. `onTrigger` fires before
    // `onSearchQueryChange` within the same editor update, so this is the source of
    // truth for entering the bare-trigger state. Composes with any caller-provided
    // handler and preserves its veto semantics: returning `false` cancels the match
    // (`MentionsPlugin.getPossibleQueryMatch` returns `null`, so no popup opens). In
    // that case there is no bare trigger to defer to, so only enter the bare-trigger
    // state when the match is actually accepted.
    const wrappedOnTriggerTypeahead = useCallback((trigger, query) => {
        const accepted = onTriggerTypeahead?.(trigger, query) ?? true;
        setIsBareMentionTrigger(accepted && !query);
        return accepted;
    }, [onTriggerTypeahead]);
    // Clear the bare-trigger state once the typeahead is no longer on a bare
    // trigger: `null` means the match disappeared entirely (trigger deleted /
    // whitespace broke it), a non-empty string means a real query is being typed.
    // An empty string is the bare trigger itself (emitted right after `onTrigger`
    // set the flag), so it is intentionally left untouched.
    const wrappedOnMentionSearchQueryChange = useCallback((query) => {
        if (query == null || query.length > 0)
            setIsBareMentionTrigger(false);
        onMentionSearchQueryChange?.(query);
    }, [onMentionSearchQueryChange]);
    return {
        onTriggerTypeahead: wrappedOnTriggerTypeahead,
        onMentionSearchQueryChange: wrappedOnMentionSearchQueryChange,
        isMentionMenuOpened,
        onMentionOpen,
        onMentionClose,
        isBareMentionTrigger,
    };
}
//# sourceMappingURL=useMentionTypeahead.js.map